'use client'

import React, {
  Fragment,
  MutableRefObject,
  ReactElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import 'lightgallery/css/lightgallery.css'
import 'lightgallery/css/lg-thumbnail.css'
import lgThumbnail from 'lightgallery/plugins/thumbnail'
import { useOnMobile } from '@/lib/hooks'
import { LightGallery } from 'lightgallery/lightgallery'
import layoutGeometry from 'justified-layout'
import lightGallery from 'lightgallery'
import { Picture, sources, VIEW_WIDTH_SIZES } from '@/components/common/picture/Picture'
import { LightGallerySettings } from 'lightgallery/lg-settings'
import { lockScrollableBody, onTouchDevice, unlockScrollableBody } from '@/lib/utils/utils'
import { createUrl } from '@/lib/utils/create-url'
import { fetchHotelImages } from '@/lib/frontent-api'
import { HotelImage } from '@/types/Misc'
import { ConfigContext } from '@/components/common/ConfigContext'
import { Currency } from '@/types/Page'

interface PictureWallProps {
  hotel: string
  closePictureWall: () => void
  scrollDestination: number | null
  closeButtonCallbackRef: MutableRefObject<(() => void) | undefined>
  imagesLoadedCallback: () => void
  imagesLoaded: boolean
  currency: Currency
}

interface ImageLayout {
  boxes: { width: number; height: number; top: number; left: number; aspectRatio: number }[]
}

function getJustifiedLayout(
  allImagesAcceptFirst: HotelImage[],
  viewWith: number,
  onMobile: boolean,
  fullWidthBreakoutRowCadence: number,
): ImageLayout {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  return layoutGeometry(
    allImagesAcceptFirst.map((image) => image.width / image.height),
    {
      containerWidth: Math.min(1024, viewWith),
      targetRowHeight: onMobile ? 140 : 220,
      fullWidthBreakoutRowCadence: fullWidthBreakoutRowCadence,
      containerPadding: { left: 0, top: 8, right: 0, bottom: 0 },
      boxSpacing: onMobile ? 10 : 16,
    },
  ) as ImageLayout
}

function roundUp(value: number): number {
  if (value <= 400) {
    return Math.ceil(value / 50) * 50
  } else if (value <= 800) {
    return Math.ceil(value / 100) * 100
  } else {
    return Math.ceil(value / 200) * 200
  }
}

function roundSize(numbers: [number, number]): [number, number] {
  const aspectRatio = numbers[0] / numbers[1]
  const roundedWidth = roundUp(numbers[0])

  return [roundedWidth, Math.round(roundedWidth / aspectRatio)]
}

function galleryOptions(
  containerRef: MutableRefObject<HTMLDivElement | null>,
  onMobile: boolean,
): LightGallerySettings {
  return {
    licenseKey: '8771FCB6-1C54-4AD5-8002-8965D3DDF459',
    plugins: [lgThumbnail],
    speed: 500,
    container: containerRef.current!,
    addClass: 'relative',
    thumbnail: !onMobile,
    counter: onMobile,
    download: false,
    prevHtml:
      '<div class="gallery-control-icon gallery-control-icon__left hidden md:block"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 9 11" fill="none">' +
      '<path d="M8 1L2 5.47692L8 10" stroke-width="1.5"/>' +
      '</svg></div>',
    nextHtml:
      '<div class="gallery-control-icon gallery-control-icon__right hidden md:block"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 9 11" fill="none">' +
      '<path d="M1 1L7 5.47692L1 10" stroke-width="1.5"/>' +
      '</svg></div>',
    thumbHeight: '80px',
    thumbWidth: 120,
    thumbMargin: 8,
    controls: !(onMobile || onTouchDevice()),
    appendCounterTo: '.lg-components',
    exThumbImage: 'data-external-thumb-image',
    width: '80%',
    currentPagerPosition: 'middle',
    alignThumbnails: 'middle',
    loop: true,
    startClass: '',
    startAnimationDuration: 0,
  }
}

function createJustifiedLayout(
  images: HotelImage[],
  viewWith: number,
  onMobile: boolean,
): ImageLayout {
  const allImagesAcceptFirst = images.slice(1)
  const firstImage = images[0]

  const layoutAcceptFirstImage = getJustifiedLayout(allImagesAcceptFirst, viewWith, onMobile, 2)
  const layoutFirstImage = getJustifiedLayout([firstImage], viewWith, onMobile, 1)

  const firstImageHeight = layoutFirstImage.boxes[0].height
  layoutAcceptFirstImage.boxes.forEach((box) => {
    box.top = box.top + firstImageHeight + (onMobile ? 10 : 16)
  })

  return { boxes: layoutFirstImage.boxes.concat(layoutAcceptFirstImage.boxes) }
}

export default function PictureWall(props: PictureWallProps): ReactElement {
  const [images, setImages] = useState<HotelImage[]>([])
  const onMobile = useOnMobile()
  const containerRef = useRef<HTMLDivElement>(null)
  const galleryRef: MutableRefObject<LightGallery | null> = useRef(null)
  const galleryContainerRef = useRef<HTMLDivElement>(null)
  const [viewWith, setViewWith] = useState(document.querySelector('#gallery')?.clientWidth || 0)
  const domain = useContext(ConfigContext)?.domain
  const [numberLoadedImages, setNumberLoadedImages] = useState(0)

  useEffect(() => {
    lockScrollableBody()
    return () => unlockScrollableBody()
  }, [])

  useEffect(() => {
    function handleResize(): void {
      setViewWith(document.querySelector('#gallery')?.clientWidth || 0)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    setTimeout(() => {
      setViewWith(document.querySelector('#gallery')?.clientWidth || 0)
    })
  })

  useEffect(() => {
    void fetchHotelImages(domain!, props.hotel, props.currency).then((images: HotelImage[]) =>
      setImages(images),
    )
  }, [])

  useEffect(() => {
    if (!props.imagesLoaded) {
      return
    }

    setTimeout(initGallery, 1000)
    return () => {
      galleryRef.current?.destroy()
    }
  }, [props.imagesLoaded])

  //scroll the div with the correct image into view
  useEffect(() => {
    if (!props.imagesLoaded) {
      return
    }

    setTimeout(() => {
      if (props.scrollDestination && !onMobile) {
        const image = document.getElementById(`image-${props.scrollDestination}`)
        if (image) {
          image.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }
    }, 1000)
  }, [props.scrollDestination, onMobile, props.imagesLoaded])

  useEffect(() => {
    if (images.length === 0) {
      return
    }

    const layout = createJustifiedLayout(images, viewWith, onMobile)
    const maxTopImageToPreload = window.innerHeight - 150
    const numberOfImagesToLoad = Math.max(
      layout.boxes.filter((b) => b.top < maxTopImageToPreload).length,
      5,
    )

    if (numberLoadedImages >= numberOfImagesToLoad) {
      props.imagesLoadedCallback()
    }
  }, [numberLoadedImages, images, viewWith, onMobile])

  function backButtonClick(): void {
    if (galleryRef.current?.lgOpened) {
      galleryRef.current?.closeGallery()
    } else {
      props.closePictureWall()
    }
  }

  props.closeButtonCallbackRef.current = backButtonClick

  function initGallery(): void {
    if (galleryRef.current || !galleryContainerRef.current) {
      return
    }

    galleryRef.current = lightGallery(
      galleryContainerRef.current,
      galleryOptions(containerRef, onMobile),
    )

    galleryContainerRef.current?.addEventListener('lgBeforeOpen', () => {
      document.querySelector('#gallery')?.classList.add('hidden')
      document.querySelector('#container')?.classList.remove('hidden')
    })
    galleryContainerRef.current?.addEventListener('lgAfterClose', () => {
      document.querySelector('#gallery')?.classList.remove('hidden')
      document.querySelector('#container')?.classList.add('hidden')
    })
  }

  if (images.length === 0) {
    return <></>
  }

  function openGallery(index: number): void {
    if (!galleryRef.current) {
      initGallery()
      setTimeout(() => galleryRef.current?.openGallery(index))
    }
  }

  const layout = createJustifiedLayout(images, viewWith, onMobile)
  const maxTopImageToPreload = window.innerHeight - 150
  const numberOfImagesToLoad = Math.max(
    layout.boxes.filter((b) => b.top < maxTopImageToPreload).length,
    5,
  )

  return (
    <>
      <div ref={containerRef} className="gallery-container t-5 hidden pt-2" id="container" />
      <div className="mx-auto max-w-screen-lg px-7 md:px-4">
        {images.length > 0 && (
          <div id="gallery" className="relative" ref={galleryContainerRef}>
            {images.map((image, index) => {
              if (!props.imagesLoaded && index > numberOfImagesToLoad) {
                return <Fragment key={index} />
              }

              return (
                <div
                  key={image.filename}
                  data-src={createUrl(image.filename, window.innerWidth)}
                  className="cursor-pointer scroll-my-12"
                  style={{ ...layout.boxes[index], position: 'absolute' }}
                  data-sub-html={image.caption}
                  data-lg-size={`${image.width}-${image.height}`}
                  data-sources={JSON.stringify(sources(image.filename, VIEW_WIDTH_SIZES, false))}
                  data-external-thumb-image={createUrl(image.filename, 116, 76)}
                  onClick={() => openGallery(index)}
                  id={`image-${index}`}
                >
                  {/*{overlayVisible && <Skeleton className="h-full w-full"/>}*/}
                  <Picture
                    priority={index <= numberOfImagesToLoad}
                    onImageLoad={() => setNumberLoadedImages((prevValue) => prevValue + 1)}
                    src={image.filename}
                    cornerRounding="rounded-lg"
                    alt={image.caption || ''}
                    size={roundSize([layout.boxes[index].width, layout.boxes[index].height])}
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>
    </>
  )
}
